import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { escalationService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { PRIORITIES } from "./constants";

const ROLES = ["SUPPORT_SUPERVISOR", "TENANT_ADMIN", "SUPPORT_ADMIN", "OPS_ADMIN"];
const EMPTY = {
  name: "", order: 1, level: 1,
  conditions: { priority: "", onResponseBreach: false, onResolutionBreach: true, inactivityMins: "" },
  action: { escalateToRole: "SUPPORT_SUPERVISOR", escalateToUserId: "", bumpPriority: false, notify: true },
  active: true, tenantId: "",
};

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function EscalationRulesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await escalationService.list({})).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...EMPTY, ...r,
      conditions: { ...EMPTY.conditions, ...(r.conditions || {}), priority: r.conditions?.priority || "", inactivityMins: r.conditions?.inactivityMins ?? "" },
      action: { ...EMPTY.action, ...(r.action || {}), escalateToUserId: r.action?.escalateToUserId || "" },
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = {
        name: form.name, order: Number(form.order), level: Number(form.level), active: form.active,
        conditions: {
          priority: form.conditions.priority || null,
          onResponseBreach: form.conditions.onResponseBreach,
          onResolutionBreach: form.conditions.onResolutionBreach,
          inactivityMins: form.conditions.inactivityMins === "" ? null : Number(form.conditions.inactivityMins),
        },
        action: {
          escalateToRole: form.action.escalateToRole || null,
          escalateToUserId: form.action.escalateToUserId || null,
          bumpPriority: form.action.bumpPriority, notify: form.action.notify,
        },
      };
      if (editing) await escalationService.update(editing.id, payload);
      else { if (isSuper) payload.tenantId = form.tenantId; await escalationService.create(payload); }
      toast.success(editing ? "Rule updated" : "Rule created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await escalationService.remove(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canManage = hasPermission("ESCALATION.MANAGE");
  const trigger = (c) => [c?.onResponseBreach && "Response breach", c?.onResolutionBreach && "Resolution breach",
    c?.inactivityMins && `Inactive ${c.inactivityMins}m`].filter(Boolean).join(", ") || "—";

  const columns = [
    { key: "order", header: "#", render: (r) => <span className="text-muted-foreground">{r.order}</span> },
    { key: "name", header: "Rule", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "cond", header: "Triggers when", render: (r) => <span className="text-xs text-muted-foreground">{trigger(r.conditions)}{r.conditions?.priority ? ` · ${r.conditions.priority}` : ""}</span> },
    { key: "act", header: "Escalates to", render: (r) => <Badge color="blue">{r.action?.escalateToRole || "user"}{r.action?.bumpPriority ? " +priority" : ""}</Badge> },
    { key: "level", header: "Level", render: (r) => <span className="text-muted-foreground">L{r.level}</span> },
    { key: "active", header: "Active", render: (r) => <Badge color={r.active ? "green" : "slate"}>{r.active ? "On" : "Off"}</Badge> },
    {
      key: "_a", header: "",
      render: (r) => canManage && (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`esc-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`esc-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="escalation-rules-page">
      <PageHeader title="Escalation Matrix" subtitle="Role-aware escalation rules triggered by SLA breach or inactivity"
        actions={canManage && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="esc-create-btn">
            <Plus className="h-4 w-4" /> New Rule
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="esc-table" empty="No escalation rules" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esc-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Escalation Rule</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="esc-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="esc-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ciq-input" /></Field>
                <Field label="Level"><input type="number" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="ciq-input" data-testid="esc-level" /></Field>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trigger conditions</p>
                <Field label="Priority (optional)">
                  <select value={form.conditions.priority} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, priority: e.target.value } })} className="ciq-input">
                    <option value="">Any priority</option>
                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </Field>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.conditions.onResponseBreach} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, onResponseBreach: e.target.checked } })} data-testid="esc-on-response" /> On response breach</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.conditions.onResolutionBreach} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, onResolutionBreach: e.target.checked } })} data-testid="esc-on-resolution" /> On resolution breach</label>
                <Field label="Inactivity minutes (optional)"><input type="number" value={form.conditions.inactivityMins} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, inactivityMins: e.target.value } })} className="ciq-input" /></Field>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Action</p>
                <Field label="Escalate to role">
                  <select value={form.action.escalateToRole} onChange={(e) => setForm({ ...form, action: { ...form.action, escalateToRole: e.target.value } })} className="ciq-input" data-testid="esc-role">
                    <option value="">—</option>
                    {ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </Field>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.action.bumpPriority} onChange={(e) => setForm({ ...form, action: { ...form.action, bumpPriority: e.target.checked } })} /> Bump priority</label>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="esc-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
